import { useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { API_BASE } from "@vorloop/api-client-react";

/**
 * Live xterm.js terminal that talks to the sandbox machine's in-image
 * shell over a WebSocket. The api-server proxies between the browser's
 * public WSS and the machine's private :8080/ws server (see
 * `apps/sandbox-image/terminal-server.mjs`).
 *
 * Mount this only when a sandbox is running — connect happens on mount.
 * Each unmount cleans up the WS + terminal so we don't leak shells.
 */
export function InteractiveTerminal({ projectId }: { projectId: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<"connecting" | "open" | "closed" | "error">("connecting");

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: "var(--font-mono), Menlo, monospace",
      fontSize: 12,
      lineHeight: 1.2,
      convertEol: true,
      theme: {
        background: "#0a0c10",
        foreground: "#d1d5db",
        cursor: "#60a5fa",
        selectionBackground: "#2563eb",
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(container);
    fit.fit();
    termRef.current = term;

    // Build the WS URL. In dev (no VITE_API_BASE) we go through the Vite
    // proxy which forwards /api/* to localhost:4099 and supports WS upgrade.
    const wsBase = API_BASE
      ? API_BASE.replace(/^https:/i, "wss:").replace(/^http:/i, "ws:")
      : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;
    const wsUrl = `${wsBase}/api/projects/${projectId}/sandbox/terminal`;

    term.writeln(`\x1b[2;37mConnecting to sandbox…\x1b[0m`);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.binaryType = "arraybuffer";

    ws.addEventListener("open", () => {
      setStatus("open");
      term.writeln(`\x1b[2;32m[connected]\x1b[0m`);
    });

    ws.addEventListener("message", (e: MessageEvent) => {
      let text: string;
      if (typeof e.data === "string") {
        text = e.data;
      } else if (e.data instanceof ArrayBuffer) {
        text = new TextDecoder().decode(new Uint8Array(e.data));
      } else {
        text = String(e.data);
      }
      term.write(text);
    });

    ws.addEventListener("close", (e) => {
      setStatus("closed");
      term.writeln(`\r\n\x1b[2;33m[connection closed: ${e.code} ${e.reason ?? ""}]\x1b[0m`);
    });

    ws.addEventListener("error", () => {
      setStatus("error");
      term.writeln(`\r\n\x1b[2;31m[connection error — sandbox unreachable]\x1b[0m`);
    });

    // Keystrokes → upstream.
    const inputDisp = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(data);
      }
    });

    // Resize when the container resizes.
    const ro = new ResizeObserver(() => {
      try { fit.fit(); } catch { /* ignore — happens during unmount */ }
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      inputDisp.dispose();
      try { ws.close(); } catch { /* ignore */ }
      term.dispose();
    };
  }, [projectId]);

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      {status !== "open" && (
        <div
          style={{
            position: "absolute",
            bottom: 4,
            right: 6,
            padding: "2px 8px",
            background: status === "error" ? "rgba(248,113,113,0.18)" : "rgba(255,255,255,0.05)",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            fontSize: 10,
            color:
              status === "error"
                ? "var(--accent-red)"
                : status === "closed"
                ? "var(--text-tertiary)"
                : "var(--text-secondary)",
          }}
        >
          {status}
        </div>
      )}
    </div>
  );
}
